import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sales } from '@/lib/supabase/sales';
import { useAuth } from '@/hooks/useAuth';
import type {
  PipelineRow, OutreachDraft, Activity, Offer, Template, AgentRun, MailSender,
} from './types';

const bff = (import.meta.env.VITE_BFF_BASE_URL as string) || '/api';

export function usePipeline() {
  return useQuery({
    queryKey: ['corp', 'pipeline'],
    queryFn: async (): Promise<PipelineRow[]> => {
      const { data, error } = await sales
        .from('v_pipeline')
        .select('*')
        .order('score', { ascending: false, nullsFirst: false })
        .limit(2000);
      if (error) throw error;
      return (data ?? []) as PipelineRow[];
    },
  });
}

export function useOutreachQueue(status = 'pending') {
  return useQuery({
    queryKey: ['corp', 'queue', status],
    queryFn: async (): Promise<OutreachDraft[]> => {
      let q = sales.from('v_outreach_queue').select('*').order('created_at', { ascending: false }).limit(500);
      if (status !== 'all') q = q.eq('status', status);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as OutreachDraft[];
    },
  });
}

export function useCompanyActivities(companyId: string | undefined) {
  return useQuery({
    enabled: !!companyId,
    queryKey: ['corp', 'activities', companyId],
    queryFn: async (): Promise<Activity[]> => {
      const { data, error } = await sales
        .from('activities')
        .select('*')
        .eq('company_id', companyId!)
        .order('occurred_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as Activity[];
    },
  });
}

export function useCompanyDrafts(companyId: string | undefined) {
  return useQuery({
    enabled: !!companyId,
    queryKey: ['corp', 'company-drafts', companyId],
    queryFn: async (): Promise<OutreachDraft[]> => {
      const { data, error } = await sales
        .from('v_outreach_queue')
        .select('*')
        .eq('company_id', companyId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as OutreachDraft[];
    },
  });
}

export function useCompanyContacts(companyId: string | undefined) {
  return useQuery({
    enabled: !!companyId,
    queryKey: ['corp', 'contacts', companyId],
    queryFn: async () => {
      const { data, error } = await sales
        .from('contacts')
        .select('*')
        .eq('company_id', companyId!)
        .order('is_primary', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export interface ContactRow {
  id: string; company_id: string; full_name: string | null; title: string | null;
  role_category: string; email: string | null; phone: string | null; linkedin_url: string | null;
  source: string; is_primary: boolean; created_at: string;
  company_name: string; company_category: string; company_city: string | null; company_status: string;
  assigned_name: string | null; is_suppressed: boolean;
}

export function useContacts() {
  return useQuery({
    queryKey: ['corp', 'all-contacts'],
    queryFn: async (): Promise<ContactRow[]> => {
      const { data, error } = await sales
        .from('v_contacts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5000);
      if (error) throw error;
      return (data ?? []) as ContactRow[];
    },
  });
}

export function useCompanyCount() {
  return useQuery({
    queryKey: ['corp', 'company-count'],
    staleTime: 30_000, retry: 0,
    queryFn: async (): Promise<number> => {
      const { count } = await sales
        .from('companies')
        .select('id', { count: 'exact', head: true })
        .eq('is_archived', false);
      return count ?? 0;
    },
  });
}

export function useUnsubCount() {
  return useQuery({
    queryKey: ['corp', 'unsub-count'],
    staleTime: 30_000, retry: 0,
    queryFn: async (): Promise<number> => {
      const { count } = await sales
        .from('suppression_list')
        .select('id', { count: 'exact', head: true })
        .eq('reason', 'unsubscribe');
      return count ?? 0;
    },
  });
}

export function useOffers() {
  return useQuery({
    queryKey: ['corp', 'offers'],
    queryFn: async (): Promise<Offer[]> => {
      const { data, error } = await sales.from('offers').select('*').order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Offer[];
    },
  });
}

export function useTemplates() {
  return useQuery({
    queryKey: ['corp', 'templates'],
    queryFn: async (): Promise<Template[]> => {
      const { data, error } = await sales.from('templates').select('*').order('stage', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Template[];
    },
  });
}

export function useMailSenders() {
  return useQuery({
    queryKey: ['corp', 'mail-senders'],
    queryFn: async (): Promise<MailSender[]> => {
      const { data, error } = await sales
        .from('mail_senders')
        .select('*')
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('address', { ascending: true });
      if (error) throw error;
      return (data ?? []) as MailSender[];
    },
  });
}

export function useAgentRuns() {
  return useQuery({
    queryKey: ['corp', 'agent-runs'],
    queryFn: async (): Promise<AgentRun[]> => {
      const { data, error } = await sales
        .from('agent_runs')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as AgentRun[];
    },
  });
}

async function postBff(path: string, body: unknown) {
  const res = await fetch(`${bff}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error || `request failed (${res.status})`);
  return json;
}

export function useRunDraftAgent() {
  return useMutation({
    mutationFn: (body: {
      outlet_id?: string; city?: string; limit?: number;
      sequence_step?: 0 | 1; dry_run?: boolean; company_ids?: string[];
    }) => postBff('/sales/corp-draft', body),
  });
}

/** Website contact enrichment (info@/hr@/careers@ from each company's own site). */
export function useRunEnrich() {
  return useMutation({
    mutationFn: (body: { outlet_id?: string; city?: string; limit?: number; company_ids?: string[]; dry_run?: boolean }) =>
      postBff('/sales/enrich', body),
  });
}

/** Stop button — flags a running job ('enrich' | 'research' | 'kiosk') to cancel. */
export function useCancelJob() {
  const { user } = useAuth();
  const actor = user ? { user_id: user.id, email: user.email } : undefined;
  return useMutation({
    mutationFn: (job: 'enrich' | 'research' | 'kiosk') =>
      postBff('/sales/crm', { action: 'cancel_job', job, actor }),
  });
}

/** Generic CRM/action mutation. Injects the acting executive for attribution. */
export function useCrmAction() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const actor = user
    ? { user_id: user.id, email: user.email, name: (user.user_metadata as any)?.full_name ?? user.email }
    : undefined;
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => postBff('/sales/crm', { ...payload, actor }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['corp'] });
      qc.invalidateQueries({ queryKey: ['sales', 'companies'] });
    },
  });
}
